import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorRef } from 'platejs/react';
import { useAppSelector } from '../../../app/hooks';
import { streamAskAicaChat } from '../../../store/dashboard/askAica/apiHelper';
import { extractEditorContext } from './serializeSelection';
import { markdownToPlate } from './markdownToPlate';
import { stripNewlinesFromFragment } from '../serializers/pasteNormalize';
import { sanitizePlateFragment } from '../serializers/sanitizePlateFragment';

export interface AskAicaMessage {
    role: 'user' | 'assistant';
    content: string;
}

/** A completed prompt→response exchange, shown in the menu transcript. */
export interface AIMenuTurn {
    id: number;
    prompt: string;
    response: string;
    /** True if this exchange was a selection-edit (drives Replace vs Insert). */
    hadSelection: boolean;
}

// ── Persisted per-document chat history ──────────────────────────────────────
// Completed turns are persisted under `plateEditor.aiHistory.<orgId>` so the
// conversation survives menu close/reopen and page refresh, and prior
// conversational turns are fed back to the model for multi-turn context.
// Bounded so localStorage + token cost stay in check.
const HISTORY_MAX_TURNS = 25; // localStorage cap
const HISTORY_THREAD_TURNS = 12; // max prior turns sent as context per request

function historyStorageKey(orgId: string | undefined): string | null {
    return orgId ? `plateEditor.aiHistory.${orgId}` : null;
}

function loadTurns(orgId: string | undefined): AIMenuTurn[] {
    const key = historyStorageKey(orgId);
    if (!key || typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        return arr
            .filter((t: any) => t && typeof t.prompt === 'string' && typeof t.response === 'string')
            .slice(-HISTORY_MAX_TURNS)
            .map((t: any, i: number) => ({ id: i + 1, prompt: t.prompt, response: t.response, hadSelection: !!t.hadSelection }));
    } catch {
        return [];
    }
}

function saveTurns(orgId: string | undefined, turns: AIMenuTurn[]): void {
    const key = historyStorageKey(orgId);
    if (!key || typeof window === 'undefined') return;
    try {
        const trimmed = turns.slice(-HISTORY_MAX_TURNS).map(({ prompt, response, hadSelection }) => ({ prompt, response, hadSelection }));
        window.localStorage.setItem(key, JSON.stringify(trimmed));
    } catch {
        /* quota / private mode — in-memory history still works */
    }
}

export interface UseAIMenuArgs {
    /**
     * Subject company org id for the askAica RAG agent. For the One Pager this
     * is the deal's `assesseeOrgId`. When absent we fall back to the investor
     * org (and, as a last resort, the localStorage value inside CHAT_CONFIG).
     */
    orgId?: string;
}

export interface UseAIMenuReturn {
    isOpen: boolean;
    open: () => void;
    /** Hide the panel WITHOUT discarding state — reopening restores everything. */
    close: () => void;
    toggle: () => void;
    prompt: string;
    setPrompt: (v: string) => void;
    streaming: boolean;
    error: string | null;
    /** Live streamed answer (markdown) for the IN-PROGRESS turn only. Once a turn
     *  completes it moves into `turns` and this clears. NOT yet in the document. */
    result: string;
    /** Completed prompt→response exchanges this session (newest last). Persists
     *  across accidental close so progress is never lost. */
    turns: AIMenuTurn[];
    /** True when the latest completed turn was a selection-edit (Replace vs Insert). */
    hadSelection: boolean;
    /** Snapshot of editor.selection captured at menu-open time. The browser
     *  paints selection on the focused element only, so when the prompt input
     *  grabs focus the editor's blue highlight disappears — the GhostSelectionOverlay
     *  re-paints a static highlight from this saved range so the user can
     *  still see what they're editing. */
    savedSelection: any | null;
    submit: () => void;
    /** Commit the latest answer: replace the selection if there was one, else insert below. */
    accept: () => void;
    /** Commit the latest answer by inserting below the cursor regardless of selection. */
    insertBelow: () => void;
    /** Discard the whole conversation and close. */
    discard: () => void;
    /** Wipe the conversation but keep the panel open for a fresh start. */
    clear: () => void;
    /** Re-run the latest turn's prompt. */
    retry: () => void;
    abort: () => void;
}

const isCollapsedSelection = (sel: any): boolean =>
    !sel ||
    !sel.anchor ||
    !sel.focus ||
    (sel.anchor.offset === sel.focus.offset && JSON.stringify(sel.anchor.path) === JSON.stringify(sel.focus.path));

export function useAIMenu({ orgId }: UseAIMenuArgs = {}): UseAIMenuReturn {
    const editor = useEditorRef();
    const userData = useAppSelector((state: any) => state.user?.userData);
    const userId: string = userData?.userId ?? '';
    const investorOrgId: string = userData?.investorOrgId ?? '';

    const [isOpen, setIsOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Live streaming buffer for the in-progress turn. On completion the
    // exchange is pushed into `turns` and this clears. NOTHING is written to the
    // document until the user accepts.
    const [result, setResult] = useState('');
    // Completed exchanges this session. Survives close/reopen so an accidental
    // dismiss never loses the conversation.
    const [turns, setTurns] = useState<AIMenuTurn[]>([]);
    // Mirror of savedSelectionRef as state — exposed in the hook return so
    // the GhostSelectionOverlay can re-render its highlight rects when the
    // captured selection changes (open/close transitions).
    const [savedSelection, setSavedSelection] = useState<any | null>(null);

    // Cleanup fn returned by streamAskAicaChat — calling it aborts the XHR.
    const abortRef = useRef<(() => void) | null>(null);
    // Set when the user cancels — lets us swallow the status-0 onError that
    // XHR.abort() triggers so a deliberate Stop doesn't surface as an error.
    const userAbortRef = useRef(false);
    // False once unmounted — the async stream callbacks below bail on it so a
    // readOnly flip / parent unmount mid-stream can't setState on a dead
    // component (the AIMenu is mounted conditionally on `!readOnly`).
    const mountedRef = useRef(true);
    // Snapshot of editor.selection captured the moment the menu OPENS. The
    // input steals focus immediately, clearing editor.selection — we restore
    // from this snapshot to extract the selected text and to commit later.
    const savedSelectionRef = useRef<any>(null);
    // Monotonic id for transcript turns (Date.now() is unavailable in some
    // sandboxes and overkill — a counter is enough for React keys).
    const turnIdRef = useRef(0);

    // Per-document chat history: hydrate on mount + whenever the document
    // (orgId) changes, and persist on every change. Keyed by the subject org so
    // different deals keep separate threads.
    const effectiveOrgId = orgId || investorOrgId;
    useEffect(() => {
        const loaded = loadTurns(effectiveOrgId);
        turnIdRef.current = loaded.length;
        setTurns(loaded);
    }, [effectiveOrgId]);
    useEffect(() => {
        saveTurns(effectiveOrgId, turns);
    }, [effectiveOrgId, turns]);

    const stopStream = useCallback(() => {
        if (abortRef.current) {
            userAbortRef.current = true;
            abortRef.current();
            abortRef.current = null;
        }
    }, []);

    // Abort any in-flight stream on unmount and mark the component dead so the
    // stream callbacks stop short of setState. Order matters: clear the flag
    // before aborting, since XHR.abort() can invoke onError synchronously.
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            stopStream();
        };
    }, [stopStream]);

    const captureSelection = useCallback(() => {
        const ed: any = editor;
        // Deep clone — Slate selection objects are not safe to hold across
        // editor mutations. JSON round-trip is fine (pure-value tree).
        const snap = ed?.selection ? JSON.parse(JSON.stringify(ed.selection)) : null;
        savedSelectionRef.current = snap;
        setSavedSelection(snap);
    }, [editor]);

    // Reset only the in-progress input/preview — does NOT touch the persisted
    // conversation history. Used after a commit and on discard.
    const resetTransient = useCallback(() => {
        stopStream();
        setPrompt('');
        setError(null);
        setResult('');
        setStreaming(false);
    }, [stopStream]);

    // Wipe the ENTIRE conversation history (in-memory + persisted) plus the
    // in-progress state. This is the explicit "Clear" action; commit/discard
    // deliberately keep the rest of the history.
    const clear = useCallback(() => {
        resetTransient();
        setTurns([]);
        const key = historyStorageKey(effectiveOrgId);
        if (key && typeof window !== 'undefined') {
            try {
                window.localStorage.removeItem(key);
            } catch {
                /* defensive */
            }
        }
    }, [resetTransient, effectiveOrgId]);

    const open = useCallback(() => {
        setError(null);
        captureSelection();
        setIsOpen(true);
    }, [captureSelection]);

    // Hide the panel but PRESERVE all state (prompt, turns, selection). Any
    // in-flight stream is aborted so it doesn't keep running invisibly.
    const close = useCallback(() => {
        setIsOpen(false);
        stopStream();
        setStreaming(false);
    }, [stopStream]);

    const toggle = useCallback(() => {
        setIsOpen((prev) => {
            if (prev) {
                // Closing — keep state, just stop any stream.
                stopStream();
                setStreaming(false);
            } else {
                // Opening — capture the fresh selection; keep the transcript.
                setError(null);
                captureSelection();
            }
            return !prev;
        });
    }, [captureSelection, stopStream]);

    const abort = useCallback(() => {
        stopStream();
        setStreaming(false);
    }, [stopStream]);

    const runStream = useCallback(
        (promptText: string) => {
            const text = promptText.trim();
            if (!text) return;

            const ed: any = editor;
            const savedSel = savedSelectionRef.current;
            if (savedSel) {
                try {
                    ed.tf?.select?.(savedSel);
                } catch {
                    /* selection restore failed */
                }
            }

            const ctx = extractEditorContext(editor);
            const selecting = !!(ctx.selectedText && savedSel && !isCollapsedSelection(savedSel));

            const userParts: string[] = [];
            if (selecting) {
                // Selection-edit mode: ask AICA to revise the selected text and
                // return ONLY the revised text, so Replace swaps it cleanly.
                userParts.push(`Revise the following text and return ONLY the revised version, with no preamble:\n${ctx.selectedText}\n`);
                if (ctx.surroundingContext) userParts.push(`Document context:\n${ctx.surroundingContext}\n`);
                userParts.push(`Instruction: ${text}`);
            } else {
                if (ctx.surroundingContext) userParts.push(`Document context:\n${ctx.surroundingContext}\n`);
                userParts.push(`Request: ${text}`);
            }
            const userMessage: AskAicaMessage = { role: 'user', content: userParts.join('\n') };

            // Conversational submits ride on the persisted history (multi-turn
            // context, capped for token cost); selection-edits stay single-turn
            // and are excluded from the context thread.
            const priorTurns = turns.filter((t) => !t.hadSelection).slice(-HISTORY_THREAD_TURNS);
            const historyMessages: AskAicaMessage[] = priorTurns.flatMap((t) => [
                { role: 'user' as const, content: t.prompt },
                { role: 'assistant' as const, content: t.response },
            ]);
            const thread: AskAicaMessage[] = selecting ? [userMessage] : [...historyMessages, userMessage];

            const org_id = orgId || investorOrgId;

            // Latest cumulative content, captured for the turn push on complete
            // (the onComplete callback can't read the freshest `result` state).
            let finalContent = '';

            userAbortRef.current = false;
            setStreaming(true);
            setError(null);
            setResult('');

            const abortFn = streamAskAicaChat(
                thread,
                [], // sources: web search OFF for the in-editor assistant
                userId,
                org_id,
                (content, _suggestedQuestions, _reasoning, _hasNewReasoning, _isFirst, metadata) => {
                    if (!mountedRef.current) return;
                    if (metadata?.error) {
                        console.error('[Plate AI] askAica reported error:', metadata.errorMessage);
                        setError(metadata.errorMessage || 'AICA returned an error');
                        return;
                    }
                    if (!content) return; // reasoning-only frame — nothing to preview yet
                    finalContent = content;
                    setResult(content);
                },
                () => {
                    if (!mountedRef.current) return;
                    setStreaming(false);
                    abortRef.current = null;
                    // Move the finished exchange into the transcript and reset the
                    // input + live buffer for the next prompt. Skip if the stream
                    // produced nothing (e.g. aborted before any content).
                    if (finalContent) {
                        const turn: AIMenuTurn = {
                            id: (turnIdRef.current += 1),
                            prompt: text,
                            response: finalContent,
                            hadSelection: selecting,
                        };
                        setTurns((prev) => [...prev, turn]);
                        setResult('');
                        setPrompt('');
                    }
                },
                (err: any) => {
                    if (!mountedRef.current) return;
                    abortRef.current = null;
                    setStreaming(false);
                    if (userAbortRef.current) {
                        userAbortRef.current = false;
                        return;
                    }
                    console.error('[Plate AI] stream error:', err);
                    setError(err?.message ?? 'AI request failed');
                },
            );
            abortRef.current = abortFn;
        },
        [editor, orgId, userId, investorOrgId, turns],
    );

    const submit = useCallback(() => {
        if (!prompt.trim() || streaming) return;
        runStream(prompt);
    }, [prompt, streaming, runStream]);

    const retry = useCallback(() => {
        const last = turns[turns.length - 1];
        if (streaming || !last) return;
        runStream(last.prompt);
    }, [streaming, turns, runStream]);

    const lastTurn = turns[turns.length - 1];

    const commit = useCallback(
        (mode: 'replace' | 'insertBelow') => {
            const ed: any = editor;
            const text = lastTurn?.response ?? '';
            const nodes = sanitizePlateFragment(stripNewlinesFromFragment((text ? (markdownToPlate(ed, text) as any[]) : []) || []));
            if (!nodes || nodes.length === 0) {
                clear();
                setIsOpen(false);
                return;
            }
            const savedSel = savedSelectionRef.current;
            try {
                if (mode === 'replace' && savedSel && !isCollapsedSelection(savedSel)) {
                    ed.tf?.select?.(savedSel);
                    ed.tf?.delete?.();
                    ed.tf?.insertFragment?.(nodes);
                } else {
                    // Insert below the block the cursor/selection started in.
                    const blockIndex: number = (savedSel?.anchor?.path?.[0] as number | undefined) ?? (ed.children?.length ?? 1) - 1;
                    const at = blockIndex + 1;
                    nodes.forEach((node: any, i: number) => {
                        try {
                            ed.tf.insertNodes(node, { at: [at + i] });
                        } catch (err) {
                            console.error('[Plate AI] insertNodes failed at', [at + i], err);
                        }
                    });
                }
            } catch (err) {
                console.error('[Plate AI] commit failed:', err);
            }
            // The edit is committed — reset the input but KEEP the conversation
            // in history, then close.
            resetTransient();
            setIsOpen(false);
        },
        [editor, lastTurn, resetTransient],
    );

    const accept = useCallback(() => commit(lastTurn?.hadSelection ? 'replace' : 'insertBelow'), [commit, lastTurn]);
    const insertBelow = useCallback(() => commit('insertBelow'), [commit]);
    const discard = useCallback(() => {
        // Drop just the un-inserted last answer; keep the rest of the history.
        setTurns((prev) => prev.slice(0, -1));
        resetTransient();
        setIsOpen(false);
    }, [resetTransient]);

    return {
        isOpen,
        open,
        close,
        toggle,
        prompt,
        setPrompt,
        streaming,
        error,
        result,
        turns,
        hadSelection: lastTurn?.hadSelection ?? false,
        savedSelection,
        submit,
        accept,
        insertBelow,
        discard,
        clear,
        retry,
        abort,
    };
}
